// Copyright 2022, Imprivata, Inc.  All rights reserved.
// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { useEffect, useRef, useState } from 'react';
import { Flex, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateDigitalIdSessionMutation,
  useCreateInquiryMutation,
  useEnrollIdentityCredentialMutation,
} from '../../api/hooks';
import type { CreateSessionRequest } from '../../api/digitalCredentialsClient';
import { Button, HexagonSpinner, Snackbar } from '@imprivata-cloud/patient-access-ui-components';
import { usePlatform } from '../../context/platform/hooks';
import { generateBaseAttributes, tracingService } from '../../tracing';
import { callDigitalCredentialsApi } from '../../utils/DigitalCredentialsHelper';
import { navigateWithContext } from '@utils/NavigationHelper';
import { useTranslation } from 'react-i18next';
import './AccountPage.less';

const handlePrivacyPolicyClick = () => {
  window.open('https://www.imprivata.com/legal/end-user-privacy-notice', '_blank', 'noopener,noreferrer');
};
const AccountPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { fromVerifyFailure } = location.state || {};
  const { featureFlags } = usePlatform();
  const { createInquiry, isPending, isSuccess, reset } = useCreateInquiryMutation();

  // Apple Digital ID state
  const [appleIdError, setAppleIdError] = useState<string | undefined>(undefined);
  const [isAppleIdLoading, setIsAppleIdLoading] = useState(false);
  const isHandlingAppleIdRef = useRef(false);

  // Enrollment mutation
  const { enrollIdentityCredential, apiError: enrollApiError } = useEnrollIdentityCredentialMutation({
    onSuccess: (patientId) => {
      // End tracing span for enrollment
      tracingService.endSpan({ status: 'success', patient_id: patientId });
      setIsAppleIdLoading(false);
      isHandlingAppleIdRef.current = false;
      navigateWithContext(navigate, `/enroll?patientId=${patientId}`);
    },
  });


  // Session creation mutation
  const { createSession, apiError: sessionApiError } = useCreateDigitalIdSessionMutation({
    onSuccess: async (session) => {
      // End session creation span
      tracingService.endSpan({ status: 'success' });

      // Start DC API call
      try {
        const deviceResponse = await callDigitalCredentialsApi(
          session.deviceRequest,
          session.encryptionInfo,
        );

        // Start enrollment span
        tracingService.startSpan(
          'apple_digital_id_enroll',
          generateBaseAttributes(tracingService.getWorkflowId()),
        );

        // Call enrollment
        enrollIdentityCredential({
          sessionId: session.sessionId,
          deviceResponse,
        });
      } catch (error) {
        setIsAppleIdLoading(false);
        isHandlingAppleIdRef.current = false;
        const err = error as Error;

        // Map DC API errors
        if (err.name === 'NotSupportedError' || err.message.includes('NotSupportedError')) {
          setAppleIdError(t('accountPage.apple-id-unavailable'));
        } else if (err.name === 'AbortError' || err.name === 'NotAllowedError') {
          setAppleIdError(t('accountPage.apple-id-cancelled'));
        } else {
          setAppleIdError(t('accountPage.apple-id-network-error'));
        }
      }
    },
  });

  useEffect(() => {
    if (sessionApiError) {
      setIsAppleIdLoading(false);
      isHandlingAppleIdRef.current = false;
      tracingService.endSpan(
        { status: 'error', error_code: sessionApiError.status },
        new Error(`Session creation failed with status ${sessionApiError.status}`),
      );
      setAppleIdError(t('accountPage.apple-id-session-failed'));
    }
  }, [sessionApiError, t]);

  useEffect(() => {
    if (enrollApiError) {
      setIsAppleIdLoading(false);
      isHandlingAppleIdRef.current = false;
      tracingService.endSpan(
        { status: 'error', error_code: enrollApiError.error?.code },
        new Error(enrollApiError.error?.message || 'Enrollment failed'),
      );
      const errorCode = enrollApiError.error?.code;
      if (errorCode === 'session-expired' || errorCode === 'session-id-not-found') {
        setAppleIdError(t('accountPage.apple-id-session-expired'));
      } else if (errorCode === 'already-enrolled') {
        setAppleIdError(t('accountPage.apple-id-already-enrolled'));
      } else {
        setAppleIdError(t('accountPage.apple-id-enrollment-failed'));
      }
    }
  }, [enrollApiError, t]);


  const handleAppleId = () => {
    // Prevent concurrent button clicks
    if (isHandlingAppleIdRef.current) {
      return;
    }
    isHandlingAppleIdRef.current = true;

    setIsAppleIdLoading(true);
    setAppleIdError(undefined);

    // Start tracing span for session creation
    tracingService.startSpan(
      'apple_digital_id_create_session',
      generateBaseAttributes(tracingService.getWorkflowId()),
    );

    const request: CreateSessionRequest = {
      origin: globalThis.location.origin,
      documentTypes: [
        {
          docType: 'org.iso.18013.5.1.mDL',
          nameSpaces: {
            'org.iso.18013.5.1': [
              { name: 'family_name', intentToRetain: false },
              { name: 'given_name', intentToRetain: false },
              { name: 'birth_date', intentToRetain: false },
              { name: 'portrait', intentToRetain: false },
              { name: 'document_number', intentToRetain: false },
            ],
          },
        },
        {
          docType: 'org.iso.23220.photoid.1',
          nameSpaces: {
            'org.iso.23220.1': [
              { name: 'family_name_unicode', intentToRetain: false },
              { name: 'given_name_unicode', intentToRetain: false },
              { name: 'family_name_latin1', intentToRetain: false },
              { name: 'given_name_latin1', intentToRetain: false },
              { name: 'birth_date', intentToRetain: false },
              { name: 'portrait', intentToRetain: false },
              { name: 'document_number', intentToRetain: false },
            ],
          },
        },
      ],
    };

    createSession(request);
  };

  useEffect(() => {
    // Reset mutation state on component mount, used when navigating back to this page
    const handler = () => reset();
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);
  if (isPending || isSuccess) {
    return (
      <Flex align="center" justify="center" vertical={true} className="h-100">
        <Typography.Title
          level={3}
          className="text-center mb-24 title-light"
          data-testid="loading-title"
        >
          {t('accountPage.need-id-selfie-verify-identity')}
        </Typography.Title>
        <HexagonSpinner data-testid="loading-spinner" />
      </Flex>
    );
  }
  return (
    <Flex align="center" justify="center" vertical={true} className="h-100">
      <Typography.Title level={3} className="text-center mb-24 title-light" data-testid="instructions-title">
        {fromVerifyFailure ? t('accountPage.verify-failed') : t('accountPage.verify-successful')}
      </Typography.Title>
      <Typography.Text className="text-center mb-24" data-testid="instructions-text">
        {t('accountPage.create-account-capture-id-selfie-verify')}
      </Typography.Text>
      <Flex vertical className="w-100" gap={16}>
        <Button
          variant="secondary"
          label={t('accountPage.imprivata-privacy-policy')}
          onClick={handlePrivacyPolicyClick}
          className="w-100"
          data-testid="privacy-policy-button"
        />
        {featureFlags.APPLE_DIGITAL_ID_ENABLED && (
          <>
            <Button
              variant="secondary"
              onClick={handleAppleId}
              disabled={isAppleIdLoading}
              className="w-100 apple-id-button"
              data-testid="apple-id-button"
            >
              <Flex align="center" justify="center" gap={8}>
                <img src="/assets/wallet.svg" alt="" width="24" height="24" />
                <span>{t('accountPage.verify-with-apple-id')}</span>
              </Flex>
            </Button>
            {appleIdError && (
              <Snackbar variant="warning" title={appleIdError} data-testid="apple-id-error" />
            )}
            <Typography.Text className="text-center" style={{ margin: '8px 0' }}>
              {t('accountPage.or')}
            </Typography.Text>
          </>
        )}
        <Button
          variant="primary"
          label={t('accountPage.continue')}
          onClick={() => createInquiry()}
          className="w-100"
          autoFocus
          data-testid="continue-button"
        />
      </Flex>
    </Flex>
  );
};
export default AccountPage;
